// ── Operation registry ────────────────────────────────────────────────────────
export const OPERATIONS = {
  ADDITION:       'addition',
  SUBTRACTION:    'subtraction',
  MULTIPLICATION: 'multiplication',
  DIVISION:       'division',
};

export const OPERATION_LABEL = {
  [OPERATIONS.ADDITION]:       'Addition',
  [OPERATIONS.SUBTRACTION]:    'Subtraction',
  [OPERATIONS.MULTIPLICATION]: 'Multiplication',
  [OPERATIONS.DIVISION]:       'Division',
};

export const OPERATION_SYMBOL = {
  [OPERATIONS.ADDITION]:       '+',
  [OPERATIONS.SUBTRACTION]:    '−',
  [OPERATIONS.MULTIPLICATION]: '×',
  [OPERATIONS.DIVISION]:       '÷',
};

// Distinct per-operation accent colors, reusing the app's existing tokens
// where they already fit (brand maroon, success green, warning amber).
export const OPERATION_COLOR = {
  [OPERATIONS.ADDITION]:       '#8B2635',
  [OPERATIONS.SUBTRACTION]:    '#2563EB',
  [OPERATIONS.MULTIPLICATION]: '#059669',
  [OPERATIONS.DIVISION]:       '#D97706',
};

// ── Grade levels ──────────────────────────────────────────────────────────────
// Every operation is always selectable regardless of grade — grade level here
// is only used to look up a *recommended* number range (see DEFAULT_RANGES),
// it never restricts what a therapist can pick for their client.
export const GRADE_LEVELS = [
  { id: 'K', label: 'Kindergarten' },
  { id: '1', label: '1st Grade' },
  { id: '2', label: '2nd Grade' },
  { id: '3', label: '3rd Grade' },
  { id: '4', label: '4th Grade' },
  { id: '5', label: '5th Grade' },
  { id: '6', label: '6th Grade' },
];

// ── Recommended operand ranges per grade + operation ─────────────────────────
// Grounded in the Common Core State Standards for Mathematics fact-fluency
// benchmarks (the standard each row is drawn from is noted below), not
// arbitrary numbers. Addition/subtraction/multiplication ranges apply to both
// operands; division ranges apply to the divisor and quotient — the dividend
// is derived (dividend = divisor * quotient) so answers are always whole
// numbers.
export const DEFAULT_RANGES = {
  // K.OA.A.5 — fluently add and subtract within 5.
  K: {
    [OPERATIONS.ADDITION]: { min: 0, max: 5 },
  },
  // 1.OA.C.6 — demonstrate fluency for addition and subtraction within 10.
  1: {
    [OPERATIONS.ADDITION]:    { min: 0, max: 10 },
    [OPERATIONS.SUBTRACTION]: { min: 0, max: 10 },
  },
  // 2.OA.B.2 — fluently add/subtract within 20; know from memory all sums of
  // two one-digit numbers.
  2: {
    [OPERATIONS.ADDITION]:    { min: 0, max: 20 },
    [OPERATIONS.SUBTRACTION]: { min: 0, max: 20 },
  },
  // 3.OA.C.7 — fluently multiply/divide within 100, know from memory all
  // products of two one-digit numbers (extended here to 12 to match the
  // traditional times-table range most curricula pair with this standard).
  // 3.NBT.A.2 — fluently add/subtract within 1000.
  3: {
    [OPERATIONS.ADDITION]:       { min: 0, max: 1000 },
    [OPERATIONS.SUBTRACTION]:    { min: 0, max: 1000 },
    [OPERATIONS.MULTIPLICATION]: { min: 0, max: 12 },
    [OPERATIONS.DIVISION]:       { min: 1, max: 12 },
  },
  // 4.NBT.B.4 — fluently add/subtract multi-digit whole numbers. Multiplication
  // and division facts stay at the 3rd-grade range for maintenance — by 4th
  // grade CCSS (4.NBT.B.5/6) moves on to multi-digit algorithms rather than
  // new single-digit facts, which don't suit a flash-card format.
  4: {
    [OPERATIONS.ADDITION]:       { min: 0, max: 1000 },
    [OPERATIONS.SUBTRACTION]:    { min: 0, max: 1000 },
    [OPERATIONS.MULTIPLICATION]: { min: 0, max: 12 },
    [OPERATIONS.DIVISION]:       { min: 1, max: 12 },
  },
  // 5.NBT.B.5/6 cover multi-digit multiplication/division algorithms, not new
  // flash-card-style facts, so this keeps the 3rd-grade fact range for
  // maintenance practice.
  5: {
    [OPERATIONS.ADDITION]:       { min: 0, max: 1000 },
    [OPERATIONS.SUBTRACTION]:    { min: 0, max: 1000 },
    [OPERATIONS.MULTIPLICATION]: { min: 0, max: 12 },
    [OPERATIONS.DIVISION]:       { min: 1, max: 12 },
  },
  // 6.NS.B.2/3 cover the standard algorithm for multi-digit and decimal
  // operations, not new whole-number facts — same maintenance range as above.
  6: {
    [OPERATIONS.ADDITION]:       { min: 0, max: 1000 },
    [OPERATIONS.SUBTRACTION]:    { min: 0, max: 1000 },
    [OPERATIONS.MULTIPLICATION]: { min: 0, max: 12 },
    [OPERATIONS.DIVISION]:       { min: 1, max: 12 },
  },
};

export const getGradeLevel = (gradeId) => GRADE_LEVELS.find(g => g.id === String(gradeId));

export const getDefaultRange = (gradeId, operation) =>
  DEFAULT_RANGES[gradeId]?.[operation] || { min: 0, max: 12 };
